/**
 * Màn hình 1.4: Xử lý Sơ bộ (Thư ký Tòa án)
 * Sub-menu nằm trên sidebar chính (navy); trang chỉ có breadcrumb + tabs + bảng/Split View.
 */
import { useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material'
import {
  ExpandMore,
  Fullscreen,
  Download,
  CheckCircle,
  RadioButtonUnchecked,
  ArrowBack,
  Assignment,
} from '@mui/icons-material'
import { useGetCasesQuery, useUpdateCaseStatusMutation } from '../../store/api/casesApi'
import type { Case } from '../../types/case'

const CONTENT_BG = '#F5F6FA'

const TAB_PARAM_MAP: Record<string, number> = { new: 0, processing: 1, archive: 2, search: 3 }
const TAB_INDEX_TO_PARAM = ['new', 'processing', 'archive', 'search'] as const

const CATEGORY_LABELS: Record<string, string> = {
  marriage_family: 'Hôn nhân & Gia đình',
  civil: 'Dân sự',
  business: 'Kinh doanh',
}

const CHECKLIST_ITEMS: { id: keyof ChecklistState; label: string; tooltip: string }[] = [
  { id: 'parties_ok', label: 'Thông tin đương sự đầy đủ', tooltip: 'Họ tên, địa chỉ, CCCD/CMND nguyên đơn và bị đơn đầy đủ theo mẫu.' },
  { id: 'content_ok', label: 'Nội dung đơn đủ điều kiện', tooltip: 'Nội dung yêu cầu rõ ràng, đúng thể thức, có chữ ký.' },
  { id: 'evidence_ok', label: 'Tài liệu chứng cứ', tooltip: 'Tài liệu chứng cứ đính kèm (nếu có) đúng quy định.' },
  { id: 'form_ok', label: 'Đơn đúng thể thức', tooltip: 'Đơn khởi kiện đúng mẫu, đủ thành phần theo quy định tố tụng.' },
]

type ChecklistState = { parties_ok: boolean; content_ok: boolean; evidence_ok: boolean; form_ok: boolean }

const DOC_TYPES = ['Đơn khởi kiện', 'CMND/CCCD', 'Giấy tờ tài sản', 'Chứng cứ khác']

export default function PreliminaryProcessingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: cases = [], refetch: refetchCases } = useGetCasesQuery()
  const [updateCaseStatus, { isLoading: updatingStatus }] = useUpdateCaseStatusMutation()
  const tabParam = searchParams.get('tab') ?? 'new'
  const tabValue = TAB_PARAM_MAP[tabParam] ?? 0
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)
  const [leftPanelWidth, setLeftPanelWidth] = useState(55)
  const [checklist, setChecklist] = useState<ChecklistState>({ parties_ok: false, content_ok: false, evidence_ok: false, form_ok: false })
  const [checklistNotes, setChecklistNotes] = useState<Record<string, string>>({})
  const [docType, setDocType] = useState(DOC_TYPES[0])
  const [returnModalOpen, setReturnModalOpen] = useState(false)
  const [returnReason, setReturnReason] = useState('Thiếu tài liệu đính kèm')
  const [supplementModalOpen, setSupplementModalOpen] = useState(false)
  const [supplementContent, setSupplementContent] = useState('Đề nghị đương sự bổ sung: (1) Bản sao CMND/CCCD hai bên; (2) Giấy chứng nhận kết hôn. Nộp trong thời hạn 07 ngày làm việc.')
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [dragging, setDragging] = useState(false)

  const filedCases = cases.filter((c) => c.status === 'filed')
  const processingCases = cases.filter((c) => ['accepted', 'pre_trial'].includes(c.status))
  const completedCases = cases.filter((c) => c.status === 'closed' || c.status === 'trial')
  const tabLists = [filedCases, processingCases, completedCases, []] as Case[][]
  const currentList = tabLists[Math.min(tabValue, 3)] ?? []
  const isSearchTab = tabValue === 3
  const selectedCase = selectedCaseId ? cases.find((c) => c.id === selectedCaseId) : null
  const checklistCount = [checklist.parties_ok, checklist.content_ok, checklist.evidence_ok, checklist.form_ok].filter(Boolean).length
  const allChecked = checklistCount === 4

  const handleOpenFile = useCallback((caseId: string) => setSelectedCaseId(caseId), [])
  const handleCloseSplit = useCallback(() => setSelectedCaseId(null), [])
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setSelectedCaseId(null)
    setSearchParams({ tab: TAB_INDEX_TO_PARAM[newValue] ?? 'new' })
  }, [setSearchParams])

  // Khi chuyển sub-menu (sidebar) → URL đổi tab → thoát hồ sơ đang mở, hiện lại danh sách
  useEffect(() => {
    setSelectedCaseId(null)
  }, [tabParam])

  useEffect(() => {
    const t = setInterval(() => {
      if (selectedCaseId && (checklistCount > 0 || Object.values(checklistNotes).some(Boolean))) {
        setToast({ open: true, message: 'Đã lưu nháp' })
      }
    }, 30000)
    return () => clearInterval(t)
  }, [selectedCaseId, checklistCount, checklistNotes])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault()
        setToast({ open: true, message: 'Đã lưu nháp' })
      }
      if (e.ctrlKey && e.key === 'ArrowRight') {
        e.preventDefault()
        const idx = currentList.findIndex((c) => c.id === selectedCaseId)
        if (idx >= 0 && idx < currentList.length - 1) handleOpenFile(currentList[idx + 1].id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [currentList, selectedCaseId, handleOpenFile])

  const handleResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setDragging(true)
    const startX = e.clientX
    const startW = leftPanelWidth
    const onMove = (e2: MouseEvent) => {
      const dx = e2.clientX - startX
      const delta = (dx / window.innerWidth) * 100
      setLeftPanelWidth(Math.min(70, Math.max(30, startW + delta)))
    }
    const onUp = () => {
      setDragging(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [leftPanelWidth])

  return (
    <Box sx={{ width: '100%', minHeight: 'calc(100vh - 56px - 24px)', bgcolor: CONTENT_BG, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {!selectedCaseId ? (
        <>
          <Breadcrumbs sx={{ px: 0, py: 1.5 }}>
            <Link underline="hover" color="inherit" href="/workspace">Bảng làm việc</Link>
            <Typography color="text.primary">Xử lý sơ bộ</Typography>
          </Breadcrumbs>
          <Tabs
            value={Math.min(tabValue, 3)}
            onChange={handleTabChange}
            sx={{
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: 'primary.main' },
              '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 3 },
            }}
          >
            <Tab label="Đơn mới tiếp nhận" />
            <Tab label="Đang xử lý" />
            <Tab label="Đã hoàn tất" />
            <Tab label="Tìm kiếm" />
          </Tabs>
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {isSearchTab ? (
              <Paper sx={{ p: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Tìm kiếm hồ sơ</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Nhập mã hồ sơ, tên đương sự hoặc số CCCD để tra cứu.</Typography>
                <TextField fullWidth size="small" placeholder="Mã hồ sơ, họ tên, CCCD…" sx={{ maxWidth: 400 }} />
              </Paper>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>STT</strong></TableCell>
                      <TableCell><strong>Mã hồ sơ</strong></TableCell>
                      <TableCell><strong>Họ tên đương sự</strong></TableCell>
                      <TableCell><strong>Loại vụ việc</strong></TableCell>
                      <TableCell><strong>Ngày nộp</strong></TableCell>
                      <TableCell><strong>Trạng thái</strong></TableCell>
                      <TableCell align="right"><strong>Thao tác</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Không có đơn nào trong danh sách.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentList.map((c, idx) => (
                        <TableRow
                          key={c.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleOpenFile(c.id)}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{c.case_code}</TableCell>
                          <TableCell>{c.plaintiff_name ?? '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={CATEGORY_LABELS[c.category] ?? c.category}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{c.filed_at ? new Date(c.filed_at).toLocaleDateString('vi-VN') : '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={tabValue === 0 ? 'Chờ xử lý' : tabValue === 1 ? 'Đang xử lý' : 'Đã hoàn tất'}
                              size="small"
                              color={tabValue === 0 ? 'warning' : tabValue === 1 ? 'info' : 'success'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button variant="contained" size="small" onClick={(e) => { e.stopPropagation(); handleOpenFile(c.id) }}>
                              Mở hồ sơ
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      ) : (
        <>
          {/* Thanh thoát về danh sách */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <Button startIcon={<ArrowBack />} size="small" onClick={handleCloseSplit}>
              Quay lại danh sách
            </Button>
            {selectedCase && (
              <Typography variant="body2" color="text.secondary">
                Đang xem: {selectedCase.case_code} · {selectedCase.plaintiff_name ?? selectedCase.plaintiff_id}/{selectedCase.defendant_name ?? selectedCase.defendant_id}
              </Typography>
            )}
          </Box>
          {/* Split View */}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Panel trái: Hồ sơ đương sự */}
            <Box sx={{ width: `${leftPanelWidth}%`, display: 'flex', flexDirection: 'column', minWidth: 200, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Tài liệu</InputLabel>
                  <Select value={docType} label="Tài liệu" onChange={(e) => setDocType(e.target.value)}>
                    {DOC_TYPES.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton size="small" title="Phóng to toàn màn hình"><Fullscreen /></IconButton>
                <IconButton size="small" title="Tải xuống"><Download /></IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
                <Paper variant="outlined" sx={{ p: 3, minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">Vùng xem tài liệu (PDF viewer) — 1/5</Typography>
                </Paper>
              </Box>
              <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', typography: 'caption', color: 'text.secondary' }}>
                Số trang: 5 · Kích thước: 1.2 MB · Ngày nộp: {selectedCase?.filed_at ? new Date(selectedCase.filed_at).toLocaleDateString('vi-VN') : '—'}
              </Box>
            </Box>

            <Box
              onMouseDown={handleResize}
              sx={{
                width: 8,
                cursor: 'col-resize',
                bgcolor: dragging ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'primary.light' },
              }}
            />

            {/* Panel phải: Checklist */}
            <Box sx={{ width: `${100 - leftPanelWidth}%`, display: 'flex', flexDirection: 'column', minWidth: 280, bgcolor: 'background.paper' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: 2, borderColor: 'primary.main' }}>
                <Typography variant="subtitle1" fontWeight={600}>Checklist kiểm tra hình thức</Typography>
              </Box>
              <Box sx={{ px: 2, py: 1 }}>
                <LinearProgress variant="determinate" value={(checklistCount / 4) * 100} sx={{ height: 8, borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">Đã kiểm tra {checklistCount}/4 tiêu chí</Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
                {CHECKLIST_ITEMS.map((item) => (
                  <Box key={item.id} sx={{ mb: 1 }}>
                    <Tooltip title={item.tooltip}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checklist[item.id]}
                            onChange={(e) => setChecklist((c) => ({ ...c, [item.id]: e.target.checked }))}
                            icon={<RadioButtonUnchecked />}
                            checkedIcon={<CheckCircle color="success" />}
                          />
                        }
                        label={item.label}
                      />
                    </Tooltip>
                    <Accordion variant="outlined" sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>Ghi chú nội bộ</AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Nhập lý do cụ thể…"
                          multiline
                          rows={2}
                          value={checklistNotes[item.id] ?? ''}
                          onChange={(e) => setChecklistNotes((n) => ({ ...n, [item.id]: e.target.value }))}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                ))}
              </Box>

              {/* Nút tác vụ fixed bottom */}
              <Box
                sx={{
                  mt: 'auto',
                  p: 2,
                  bgcolor: 'background.paper',
                  boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button variant="contained" color="error" onClick={() => setReturnModalOpen(true)}>
                  Đề xuất trả đơn
                </Button>
                <Button variant="contained" color="warning" onClick={() => setSupplementModalOpen(true)}>
                  Yêu cầu bổ sung
                </Button>
                <Tooltip title={!allChecked ? 'Vui lòng kiểm tra đủ 4 tiêu chí trước khi chấp nhận thụ lý.' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={!allChecked || updatingStatus}
                      sx={{ opacity: allChecked ? 1 : 0.6 }}
                      onClick={async () => {
                        if (!selectedCaseId || !allChecked) return
                        try {
                          await updateCaseStatus({ case_id: selectedCaseId, status: 'accepted' }).unwrap()
                          setToast({ open: true, message: 'Đã chấp nhận thụ lý. Dùng Smart Legal Tech – Soạn thảo văn bản để tạo Giấy báo nộp tạm ứng án phí và gửi đương sự.' })
                          setSelectedCaseId(null)
                          setChecklist({ parties_ok: false, content_ok: false, evidence_ok: false, form_ok: false })
                          refetchCases()
                        } catch (e) {
                          setToast({ open: true, message: 'Có lỗi khi cập nhật trạng thái.' })
                        }
                      }}
                    >
                      {updatingStatus ? 'Đang xử lý...' : 'Chấp nhận thụ lý'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={!allChecked ? 'Hoàn tất checklist rồi bấm để trình Chánh án phân công Thẩm phán.' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<Assignment />}
                      disabled={!allChecked || updatingStatus}
                      sx={{ opacity: allChecked ? 1 : 0.6 }}
                      onClick={async () => {
                        if (!selectedCaseId || !allChecked) return
                        try {
                          await updateCaseStatus({ case_id: selectedCaseId, status: 'accepted' }).unwrap()
                          setToast({ open: true, message: 'Đã trình Chánh án phân công. Vụ án đã chuyển vào danh sách chờ phân công Thẩm phán.' })
                          setSelectedCaseId(null)
                          setChecklist({ parties_ok: false, content_ok: false, evidence_ok: false, form_ok: false })
                          refetchCases()
                        } catch (e) {
                          setToast({ open: true, message: 'Có lỗi khi trình phân công.' })
                        }
                      }}
                    >
                      Trình Chánh án phân công
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="outlined" onClick={handleCloseSplit} sx={{ ml: 'auto' }}>
                  Đóng hồ sơ
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Modal Đề xuất trả đơn */}
      <Dialog open={returnModalOpen} onClose={() => setReturnModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Đề xuất trả đơn</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Lý do trả đơn</InputLabel>
            <Select value={returnReason} label="Lý do trả đơn" onChange={(e) => setReturnReason(e.target.value)} required>
              <MenuItem value="Thiếu chữ ký">Thiếu chữ ký</MenuItem>
              <MenuItem value="Thiếu tài liệu đính kèm">Thiếu tài liệu đính kèm</MenuItem>
              <MenuItem value="Nội dung không rõ ràng">Nội dung không rõ ràng</MenuItem>
              <MenuItem value="Không đúng thẩm quyền">Không đúng thẩm quyền</MenuItem>
              <MenuItem value="Khác">Khác</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Nội dung thông báo gửi đương sự (preview) sẽ hiển thị sau khi chọn lý do và xác nhận.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnModalOpen(false)}>Hủy</Button>
          <Button variant="contained" color="error" onClick={() => { setReturnModalOpen(false); setToast({ open: true, message: 'Đã gửi đề xuất trả đơn' }) }} disabled={!returnReason}>
            Xác nhận
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Yêu cầu bổ sung */}
      <Dialog open={supplementModalOpen} onClose={() => setSupplementModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yêu cầu bổ sung</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Soạn nội dung yêu cầu. Có thể tick các hạng mục thiếu từ checklist để tự điền vào nội dung.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Nội dung yêu cầu bổ sung gửi đương sự…"
            value={supplementContent}
            onChange={(e) => setSupplementContent(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplementModalOpen(false)}>Hủy</Button>
          <Button variant="contained" color="warning" onClick={() => { setSupplementModalOpen(false); setToast({ open: true, message: 'Đã gửi yêu cầu bổ sung' }) }}>
            Gửi thông báo
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="info" onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  )
}
